import { EMPTY_STR } from "@/constants/string";

/**
 * Filter form type for case match talent filtering popover.
 * Fields map to SearchTalentOptions backend DTO where possible.
 */
export type TCaseMatchFilterFormExtended = {
  talentNumber: string; // 人材管理番号
  fullName: string; // 名前
  nickName: string; // 呼び名
  hasOtherProposal: string[]; // 他提案
  nationalityId: string[]; // 国籍
  gender: string[]; // 性別
  ageFrom: string; // 年齢（開始）
  ageTo: string; // 年齢（終了）
  residenceStatusId: string[]; // 在留資格
  periodOfStayFrom: string; // 在留期限（開始）
  periodOfStayTo: string; // 在留期限（終了）
  languageTestLevelId: string[]; // 日本語能力
  partnerId: string[]; // パートナー
  personInChargeId: string[]; // 担当者
};

export type TCaseMatchDisplayColumnSetting = {
  id: string;
  label: string;
  visible: boolean;
  order: number;
};

export const defaultCaseMatchFilterValues: TCaseMatchFilterFormExtended = {
  talentNumber: "",
  fullName: "",
  nickName: "",
  hasOtherProposal: [],
  nationalityId: [],
  gender: [],
  ageFrom: "",
  ageTo: "",
  residenceStatusId: [],
  periodOfStayFrom: "",
  periodOfStayTo: "",
  languageTestLevelId: [],
  partnerId: [],
  personInChargeId: [],
};

export const defaultCaseMatchDisplayColumns: TCaseMatchDisplayColumnSetting[] = [
  { id: "talentNumber", label: "人材管理番号", visible: true, order: 1 },
  { id: "fullName", label: "名前", visible: true, order: 2 },
  { id: "nickName", label: "呼び名", visible: true, order: 3 },
  { id: "isShowAnotherCase", label: "他提案", visible: true, order: 4 },
  { id: "nationalityName", label: "国籍", visible: true, order: 5 },
  { id: "gender", label: "性別", visible: true, order: 6 },
  { id: "age", label: "年齢", visible: true, order: 7 },
  { id: "residenceStatusId", label: "在留資格", visible: true, order: 8 },
  { id: "periodOfStay", label: "在留期限", visible: true, order: 9 },
  { id: "languageCertificates", label: "日本語能力", visible: true, order: 10 },
  { id: "partner", label: "パートナー", visible: true, order: 11 },
  { id: "personInChargeId", label: "担当者", visible: true, order: 12 },
];

export const CASE_MATCH_FILTER_FIELD_LABELS: { key: keyof TCaseMatchFilterFormExtended; label: string }[] = [
  { key: "talentNumber", label: "人材管理番号" },
  { key: "fullName", label: "名前" },
  { key: "nickName", label: "呼び名" },
  { key: "hasOtherProposal", label: "他提案" },
  { key: "nationalityId", label: "国籍" },
  { key: "gender", label: "性別" },
  { key: "ageFrom", label: "年齢" },
  { key: "residenceStatusId", label: "在留資格" },
  { key: "periodOfStayFrom", label: "在留期限" },
  { key: "languageTestLevelId", label: "日本語能力" },
  { key: "partnerId", label: "パートナー" },
  { key: "personInChargeId", label: "担当者" },
];

/** Multi-select filter field keys */
export const CASE_MATCH_MULTI_SELECT_FILTER_KEYS: (keyof TCaseMatchFilterFormExtended)[] = [
  "hasOtherProposal",
  "nationalityId",
  "gender",
  "residenceStatusId",
  "languageTestLevelId",
  "partnerId",
  "personInChargeId",
];

/** Check if a filter value is empty */
export const isEmptyCaseMatchFilterValue = (value: unknown): boolean => {
  if (value === "" || value === null || value === undefined) return true;
  if (Array.isArray(value) && value.length === 0) return true;
  return false;
};

export const stripEmptyCaseMatchFilterFields = (filters: TCaseMatchFilterFormExtended): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (isEmptyCaseMatchFilterValue(value)) continue;
    result[key] = value;
  }
  return result;
};

/** Normalize legacy scalar filter values to arrays for backward compatibility */
export const normalizeCaseMatchMultiSelectFields = (filters: Partial<Record<string, unknown>>): Partial<TCaseMatchFilterFormExtended> => {
  const result = { ...filters };
  for (const key of CASE_MATCH_MULTI_SELECT_FILTER_KEYS) {
    const value = result[key];
    if (value === undefined) continue;
    if (Array.isArray(value)) continue;
    if (value === "" || value === null) {
      result[key] = [];
    } else {
      result[key] = [String(value)];
    }
  }
  return result as Partial<TCaseMatchFilterFormExtended>;
};

export const FILTER_LABEL_MAX_LENGTH = 30;

/** Locked columns that cannot be toggled off in display settings */
export const LOCKED_CASE_MATCH_COLUMN_IDS = ["fullName"];
